import { useCallback, useEffect, useState } from "react";
import api from "../utils/api";
import { useAuth } from "../AuthContext";
import { setBlockedIds } from "../utils/blockedStore";

// The signed-in user's block list, shared by every component on the page (one fetch).
// Blocking hides that person's comments and content from you and stops private messages
// both ways; the server enforces the messaging part.
let store = { forUser: null, list: [], loaded: false };
const listeners = new Set();
const emit = () => {
  setBlockedIds(store.list.map((b) => b.userId));
  listeners.forEach((fn) => fn(store));
};

async function load(userId) {
  try {
    const r = await api.get("/api/blocks");
    store = { forUser: userId, list: Array.isArray(r.data) ? r.data : [], loaded: true };
  } catch {
    store = { forUser: userId, list: [], loaded: true };
  }
  emit();
}

export default function useBlockedUsers() {
  const { user } = useAuth();
  const userId = user?.userId || null;
  const [state, setState] = useState(store);

  useEffect(() => {
    listeners.add(setState);
    if (!userId) {
      if (store.forUser !== null) { store = { forUser: null, list: [], loaded: true }; emit(); }
    } else if (store.forUser !== userId) {
      store = { forUser: userId, list: [], loaded: false };
      load(userId);
    }
    return () => listeners.delete(setState);
  }, [userId]);

  const block = useCallback(async (targetId) => {
    await api.post(`/api/blocks/${targetId}`);
    await load(userId);
  }, [userId]);

  const unblock = useCallback(async (targetId) => {
    await api.delete(`/api/blocks/${targetId}`);
    await load(userId);
  }, [userId]);

  const blockedIds = new Set(state.list.map((b) => Number(b.userId)));
  const isBlocked = (id) => id != null && blockedIds.has(Number(id));

  return { blocked: state.list, isBlocked, block, unblock };
}
